/**
 * Brand-specific maintenance schedules.
 *
 * Every brand hub used to render one hardcoded interval table with the brand
 * name swapped in, which Google correctly read as ~87 duplicate pages. The
 * schedule, the shop notes, the cost band and the FAQs below are all derived
 * from what is actually in the database for that brand: powertrain mix, engine
 * size, model-year span and model count. A Tesla page has no oil change, a Ram
 * page gets a diesel section, a Land Rover page gets luxury parts pricing.
 */

export interface BrandProfile {
  makeSlug: string;
  makeName: string;
  modelCount: number;
  rowCount: number;
  yearMin: number | null;
  yearMax: number | null;
  /** Share (0-1) of database rows for this brand. */
  evShare: number;
  hybridShare: number;
  dieselShare: number;
  /** Mean cylinder count across gas rows, null for EV-only brands. */
  avgCylinders: number | null;
  /** Most common EPA vehicle classes, most frequent first. */
  topClasses: string[];
}

export interface ScheduleRow {
  miles: number;
  service: string;
  shopNote: string;
}

const LUXURY_MAKES = new Set([
  "audi",
  "bmw",
  "mercedes-benz",
  "porsche",
  "land-rover",
  "jaguar",
  "lexus",
  "genesis",
  "volvo",
  "infiniti",
  "acura",
  "cadillac",
  "lincoln",
  "maserati",
  "bentley",
  "rolls-royce",
  "alfa-romeo",
  "tesla",
  "lucid",
  "polestar",
  "rivian",
]);

export function isLuxuryBrand(makeSlug: string): boolean {
  return LUXURY_MAKES.has(makeSlug);
}

/** An EV-only brand: no oil, no plugs, no belts. */
export function isElectricBrand(p: BrandProfile): boolean {
  return p.evShare >= 0.9;
}

/** Oil interval this brand's engines realistically want. */
export function oilInterval(p: BrandProfile): number {
  if (isElectricBrand(p)) return 0;
  if ((p.avgCylinders ?? 4) >= 6 || isLuxuryBrand(p.makeSlug)) return 10000;
  return 7500;
}

/** Typical independent-shop cost band for a routine service. */
export function serviceCostBand(p: BrandProfile): { low: number; high: number; label: string } {
  if (isElectricBrand(p)) return { low: 60, high: 180, label: "EV service visit" };
  if (isLuxuryBrand(p.makeSlug)) return { low: 140, high: 380, label: "oil and filter service" };
  return { low: 70, high: 160, label: "oil and filter service" };
}

function pct(n: number): number {
  return Math.round(n * 100);
}

export function buildBrandSchedule(p: BrandProfile): ScheduleRow[] {
  const name = p.makeName;

  if (isElectricBrand(p)) {
    return [
      {
        miles: 7500,
        service: "Tire rotation, tire pressure and tread check",
        shopNote: `Instant torque and battery weight chew through tires faster than a gas car of the same size. On a ${name} this is the single service you cannot skip — rotate on time or you buy tires 10,000 miles early.`,
      },
      {
        miles: 15000,
        service: "Cabin air filter, wiper blades, brake and suspension inspection",
        shopNote:
          "Regenerative braking means the friction pads barely wear, so the risk flips: calipers seize from disuse. A yearly inspection catches sticking slide pins before a rotor is ruined.",
      },
      {
        miles: 30000,
        service: "Brake fluid test and replace, coolant loop inspection",
        shopNote:
          "Brake fluid absorbs water on a schedule, not on mileage. Two to three years regardless of how little you use the pedal.",
      },
      {
        miles: 60000,
        service: "Battery thermal-management coolant service, HV cable and connector check",
        shopNote: `The high-voltage cooling loop is the part of a ${name} that decides how the pack ages. Have the coolant condition read rather than assuming it is lifetime.`,
      },
      {
        miles: 100000,
        service: "Battery state-of-health report, reduction gear fluid, suspension bushings",
        shopNote:
          "A documented pack health report is worth real money at resale, and it is the only way to know whether range loss is degradation or a bad cell module.",
      },
    ];
  }

  const oil = oilInterval(p);
  const rows: ScheduleRow[] = [
    {
      miles: oil,
      service: "Engine oil & filter, tire rotation, fluid level check",
      shopNote:
        oil >= 10000
          ? `${name} spec calls for full synthetic on a long interval, and the engines are built for it. Short-trip city driving is the exception: under 15 minutes per drive, cut this in half or the oil never gets hot enough to boil off fuel dilution.`
          : `A ${oil.toLocaleString()}-mile interval fits most ${name} engines on full synthetic. Towing, idling or short city hops push it back to 5,000.`,
    },
    {
      miles: 15000,
      service: "Cabin & engine air filters, brake inspection, wiper blades",
      shopNote:
        "Cabin filters are the most skipped item on any schedule and the most common reason A/C feels weak. Two minutes behind the glovebox on most cars.",
    },
    {
      miles: 30000,
      service: "Brake fluid, transmission fluid check, coolant inspection, spark plug check",
      shopNote:
        "Brake fluid absorbs water. Past three years it boils sooner and the pedal goes long on a long mountain descent.",
    },
  ];

  if (p.dieselShare > 0.03) {
    rows.push({
      miles: 20000,
      service: "Diesel fuel filters, DEF top-up, DPF regeneration check",
      shopNote: `${pct(p.dieselShare)}% of the ${name} configurations in our data are diesel. Those need fuel filters on their own clock and enough highway running to complete a DPF regen — a diesel used only for short trips will clog.`,
    });
  }

  if (p.hybridShare > 0.1) {
    rows.push({
      miles: 36000,
      service: "Hybrid battery cooling fan clean-out, inverter coolant inspection",
      shopNote: `${pct(p.hybridShare)}% of ${name} rows here are hybrids. Their battery fan pulls cabin air, so it fills with dust and pet hair — a blocked fan is the number one cause of premature hybrid battery failure, and cleaning it is cheap.`,
    });
  }

  rows.push({
    miles: 60000,
    service: "Transmission fluid service, spark plugs, serpentine belt, coolant",
    shopNote:
      "'Lifetime' transmission fluid means the life of the warranty. Drain and fill at 60k and the gearbox lasts roughly twice as long.",
  });

  rows.push({
    miles: 100000,
    service: "Timing belt (if equipped), water pump, plugs, full fluid replacement",
    shopNote:
      (p.avgCylinders ?? 4) >= 6
        ? `Larger ${name} engines here average ${(p.avgCylinders ?? 6).toFixed(1)} cylinders, so plug replacement is a longer job — often intake removal on a V6 or V8. Do the coils and water pump in the same visit; the labor overlaps.`
        : "Chain-driven engines skip the belt, but the water pump and thermostat still age out. Do them together — the labor overlaps.",
  });

  return rows.sort((a, b) => a.miles - b.miles);
}

/** One-paragraph brand summary used for the BLUF block and meta description. */
export function brandSummary(p: BrandProfile): string {
  const span =
    p.yearMin && p.yearMax ? `${p.yearMin}-${p.yearMax}` : "recent model years";
  const cost = serviceCostBand(p);
  if (isElectricBrand(p)) {
    return `Every ${p.makeName} in our data (${p.modelCount} models, ${span}) is electric, so there is no oil change on this schedule at all. Tires, brake fluid and the battery cooling loop are the whole maintenance story, and a routine ${cost.label} runs about $${cost.low}-$${cost.high}.`;
  }
  const mix: string[] = [];
  if (p.hybridShare > 0.1) mix.push(`${pct(p.hybridShare)}% hybrid`);
  if (p.evShare > 0.03) mix.push(`${pct(p.evShare)}% electric`);
  if (p.dieselShare > 0.03) mix.push(`${pct(p.dieselShare)}% diesel`);
  const mixText = mix.length ? ` The lineup here is ${mix.join(", ")}, which adds services a pure gas schedule does not have.` : "";
  return `Across ${p.modelCount} ${p.makeName} models and ${p.rowCount.toLocaleString()} configurations from ${span}, the oil interval that fits this brand is ${oilInterval(p).toLocaleString()} miles on full synthetic, and a routine ${cost.label} runs about $${cost.low}-$${cost.high} at an independent shop.${mixText} Keep the oil and rotation log and your factory warranty stays intact.`;
}

export function brandFaqs(p: BrandProfile): { q: string; a: string }[] {
  const cost = serviceCostBand(p);
  const faqs: { q: string; a: string }[] = [];

  if (isElectricBrand(p)) {
    faqs.push({
      q: `Does a ${p.makeName} need an oil change?`,
      a: `No. Every ${p.makeName} in our database is fully electric, so there is no engine oil, no filter and no spark plugs. The recurring items are tire rotation every 7,500 miles, cabin filter and wiper blades yearly, and brake fluid every two to three years.`,
    });
    faqs.push({
      q: `What does ${p.makeName} maintenance cost per year?`,
      a: `Budget roughly $${cost.low}-$${cost.high} for a routine visit and plan for tires sooner than you would on a gas car — the extra battery weight and instant torque typically cut tire life by 10,000-15,000 miles.`,
    });
  } else {
    faqs.push({
      q: `How often should you change the oil on a ${p.makeName}?`,
      a: `${oilInterval(p).toLocaleString()} miles on full synthetic fits the ${p.makeName} engines in our data (averaging ${(p.avgCylinders ?? 4).toFixed(1)} cylinders). Drop to 5,000 miles if most trips are under 15 minutes, if you tow, or if the car idles a lot.`,
    });
    faqs.push({
      q: `What does a ${p.makeName} service cost?`,
      a: `A routine ${cost.label} runs about $${cost.low}-$${cost.high} at an independent shop${isLuxuryBrand(p.makeSlug) ? ` — ${p.makeName} parts and specified oil grades sit at the higher end, and dealer pricing is typically 30-50% above that` : ""}. Bigger milestone services (60k transmission and plugs) run several times that.`,
    });
  }

  if (p.hybridShare > 0.1) {
    faqs.push({
      q: `Do ${p.makeName} hybrids need different maintenance?`,
      a: `Yes. ${pct(p.hybridShare)}% of the ${p.makeName} configurations here are hybrids. They use less brake pad thanks to regenerative braking, but they add a battery cooling fan that must be cleaned and inverter coolant that must be checked — usually around 36,000 miles.`,
    });
  }

  if (p.dieselShare > 0.03) {
    faqs.push({
      q: `What extra service do ${p.makeName} diesels need?`,
      a: `Diesel ${p.makeName} configurations (${pct(p.dieselShare)}% of our rows) need fuel filters roughly every 20,000 miles, DEF top-ups between services, and enough sustained highway driving to finish DPF regeneration cycles.`,
    });
  }

  faqs.push({
    q: `Can you service a ${p.makeName} outside the dealership?`,
    a: `Yes. Under the Magnuson-Moss Warranty Act any qualified shop can perform scheduled maintenance without affecting your factory ${p.makeName} warranty, as long as the correct parts and fluids are used. Keep the receipts with mileage — that is all a dealer can ask for.`,
  });

  return faqs;
}
